import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Seo } from '@/components/Seo';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Lock, Loader2, Plus } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import {
  usePassportEntries,
  useAddPassportEntry,
  useUpdatePassportEntry,
  useDeletePassportEntry,
} from '@/hooks/usePassport';
import {
  groupEntriesByCountry,
  getUniqueCountryCount,
  getHighlightedIso2Codes,
  getDestinationCountsByIso2,
  type PassportEntryRow,
} from '@/lib/passport';
import PassportWorldMap from '@/components/passport/PassportWorldMap';
import PassportSummaryLine from '@/components/passport/PassportSummaryLine';
import AddPassportEntrySheet from '@/components/passport/AddPassportEntrySheet';
import PassportCountryGroup from '@/components/passport/PassportCountryGroup';
import { trackEvent } from '@/lib/analytics';

const MyPassport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();
  const { data: entries = [], isLoading: entriesLoading } = usePassportEntries();
  const addEntry = useAddPassportEntry();
  const updateEntry = useUpdatePassportEntry();
  const deleteEntry = useDeletePassportEntry();

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<PassportEntryRow | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PassportEntryRow | null>(null);

  const hasTrackedOpen = useRef(false);
  const previousCount = useRef<number | null>(null);

  useEffect(() => {
    if (user && !hasTrackedOpen.current) {
      hasTrackedOpen.current = true;
      trackEvent('passport_opened');
    }
  }, [user]);

  const countryCount = getUniqueCountryCount(entries);
  const highlightedIso2 = getHighlightedIso2Codes(entries);
  const destinationCounts = getDestinationCountsByIso2(entries);
  const countryGroups = groupEntriesByCountry(entries);

  useEffect(() => {
    if (entriesLoading) return;
    if (previousCount.current === null) {
      previousCount.current = entries.length;
      return;
    }
    if (previousCount.current === 0 && entries.length === 1) {
      trackEvent('passport_first_entry_added');
    }
    previousCount.current = entries.length;
  }, [entries.length, entriesLoading]);

  const openAddSheet = () => {
    setEditingEntry(null);
    setSheetOpen(true);
    trackEvent('passport_entry_started');
  };

  // My Swell Breaks' own "Add to Passport" button lands here and asks the sheet to
  // open immediately -- a returning user shouldn't need a second tap to log a trip.
  // Cleared via a replaced history entry so refreshing this page never reopens it.
  useEffect(() => {
    const state = location.state as { openAdd?: boolean } | null;
    if (state?.openAdd) {
      openAddSheet();
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const openEditSheet = (entry: PassportEntryRow) => {
    setEditingEntry(entry);
    setSheetOpen(true);
  };

  const handleSubmit = (input: { countryId: string; destinationName: string; month: number }) => {
    if (editingEntry) {
      updateEntry.mutate(
        { id: editingEntry.id, ...input },
        {
          onSuccess: () => {
            trackEvent('passport_entry_edited');
            setSheetOpen(false);
          },
        }
      );
    } else {
      addEntry.mutate(input, {
        onSuccess: () => {
          trackEvent('passport_entry_added', { country_id: input.countryId, month: input.month });
          setSheetOpen(false);
        },
      });
    }
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;
    deleteEntry.mutate(pendingDelete.id, {
      onSuccess: () => trackEvent('passport_entry_deleted'),
    });
    setPendingDelete(null);
  };

  const handleSelectCountryOnMap = (iso2: string) => {
    const group = countryGroups.find((g) => g.countryIso2 === iso2);
    if (!group) return;
    document
      .getElementById(`passport-country-${group.countryIso2 ?? group.countryId}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (loading) {
    return (
      <Layout>
        <Seo title="My Surf Passport | Swell Breaks" description="Every country you've surfed, in one place." path="/my-passport" noIndex />
        <div className="container mx-auto px-4 py-20 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <Seo title="My Surf Passport | Swell Breaks" description="Every country you've surfed, in one place." path="/my-passport" noIndex />
        <section className="py-12 md:py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h1 className="font-serif text-3xl md:text-5xl font-semibold text-foreground mb-4">My Surf Passport</h1>
              <p className="text-muted-foreground max-w-lg mx-auto">
                Every country you've surfed, building into a map of your own surfing life.
              </p>
            </div>

            <Card className="max-w-lg mx-auto border-border/50">
              <CardHeader className="text-center pb-4">
                <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <Lock className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="font-serif text-2xl">Sign in to start your Passport</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-muted-foreground text-center">
                  Create an account to record where you've surfed and watch your own world map fill in.
                </p>
                <div className="flex flex-col gap-3 pt-2">
                  <Button
                    variant="default"
                    className="w-full"
                    onClick={() => navigate('/auth', { state: { from: location.pathname } })}
                  >
                    Create Account
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate('/auth', { state: { from: location.pathname } })}
                  >
                    Sign In
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <Seo title="My Surf Passport | Swell Breaks" description="Every country you've surfed, in one place." path="/my-passport" noIndex />

      <section className="pt-10 pb-6 md:pt-14">
        <div className="container mx-auto px-4 max-w-4xl">
          <Link to="/my-swell-breaks" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            ← My Swell Breaks
          </Link>
          <h1 className="font-serif text-2xl md:text-4xl font-semibold text-foreground mt-2">My Surf Passport</h1>
        </div>
      </section>

      {entriesLoading ? (
        <div className="py-16 flex justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <section className="border-y border-border/50 bg-secondary/30 py-8 md:py-14">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="-mx-4 sm:mx-0">
              <PassportWorldMap
                highlightedIso2={highlightedIso2}
                destinationCountsByIso2={destinationCounts}
                onSelectCountry={handleSelectCountryOnMap}
              />
            </div>

            <div className="mt-6 md:mt-8 md:flex md:items-end md:justify-between md:gap-8">
              <div>
                <PassportSummaryLine countryCount={countryCount} destinationCount={entries.length} />
                {entries.length === 0 && (
                  <p className="mt-2 text-sm text-muted-foreground">
                    Your surf map starts here — add the first place you've surfed.
                  </p>
                )}
              </div>
              <Button onClick={openAddSheet} size="lg" className="mt-5 md:mt-0 w-full sm:w-auto gap-2">
                <Plus className="h-4 w-4" />
                Add to Passport
              </Button>
            </div>
          </div>
        </section>
      )}

      {!entriesLoading && entries.length > 0 && (
        <section className="py-10 md:py-14">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="font-serif text-2xl text-foreground mb-2">Surf history</h2>
            <div>
              {countryGroups.map((group) => (
                <PassportCountryGroup key={group.countryId} group={group} onEdit={openEditSheet} onDelete={setPendingDelete} />
              ))}
            </div>
          </div>
        </section>
      )}

      <AddPassportEntrySheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        editingEntry={editingEntry}
        onSubmit={handleSubmit}
        isSubmitting={addEntry.isPending || updateEntry.isPending}
      />

      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this entry?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete && (
                <>
                  This removes {pendingDelete.destination_name} from your Passport. If it's your last entry for that
                  country, it will return to unvisited on your map.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default MyPassport;
